/**
 * Accessibility Hook for Animations
 * Provides comprehensive accessibility features for animated components
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useReducedMotion, useAnimationConfig } from './context';

interface AccessibilityOptions {
  announceChanges?: boolean;
  respectFocusManagement?: boolean;
  enableKeyboardNavigation?: boolean;
  autoFocus?: boolean;
  skipLinks?: boolean;
}

interface AccessibilityState {
  isReducedMotion: boolean;
  isFocusVisible: boolean;
  isKeyboardUser: boolean;
  currentFocus: Element | null;
  announcements: string[];
}

interface AccessibilityControls {
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
  focusElement: (element: Element | null) => void;
  skipToContent: () => void;
  enableFocusVisible: () => void;
  disableFocusVisible: () => void;
}

export const useAccessibility = (
  options: AccessibilityOptions = {}
): AccessibilityState & AccessibilityControls => {
  const {
    announceChanges = false,
    respectFocusManagement = true,
    enableKeyboardNavigation = true,
    autoFocus = false,
    skipLinks = true
  } = options;

  const isReducedMotion = useReducedMotion();
  const config = useAnimationConfig();
  
  const [accessibilityState, setAccessibilityState] = useState<AccessibilityState>({
    isReducedMotion,
    isFocusVisible: false,
    isKeyboardUser: false,
    currentFocus: null,
    announcements: []
  });

  const liveRegionRef = useRef<HTMLDivElement | null>(null);
  const focusHistoryRef = useRef<Element[]>([]);
  const keyboardUserRef = useRef(false);

  // Initialize live region for screen reader announcements
  useEffect(() => {
    if (!announceChanges) return;

    const createLiveRegion = (priority: 'polite' | 'assertive' = 'polite') => {
      const id = `animation-live-${priority}`;
      let region = document.getElementById(id) as HTMLDivElement;
      
      if (!region) {
        region = document.createElement('div');
        region.id = id;
        region.setAttribute('aria-live', priority);
        region.setAttribute('aria-atomic', 'true');
        region.style.position = 'absolute';
        region.style.left = '-10000px';
        region.style.width = '1px';
        region.style.height = '1px';
        region.style.overflow = 'hidden';
        document.body.appendChild(region);
      }
      
      return region;
    };

    liveRegionRef.current = createLiveRegion('polite');
    createLiveRegion('assertive'); // Create both priority levels

    return () => {
      const politeRegion = document.getElementById('animation-live-polite');
      const assertiveRegion = document.getElementById('animation-live-assertive');
      politeRegion?.remove();
      assertiveRegion?.remove();
    };
  }, [announceChanges]);

  // Keyboard navigation detection
  useEffect(() => {
    if (!enableKeyboardNavigation) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ') {
        keyboardUserRef.current = true;
        setAccessibilityState(prev => ({ ...prev, isKeyboardUser: true }));
      }
    };

    const handleMouseDown = () => {
      keyboardUserRef.current = false;
      setAccessibilityState(prev => ({ ...prev, isKeyboardUser: false }));
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [enableKeyboardNavigation]);

  // Focus management
  useEffect(() => {
    if (!respectFocusManagement) return;

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as Element;
      focusHistoryRef.current.push(target);
      
      setAccessibilityState(prev => ({
        ...prev,
        currentFocus: target,
        isFocusVisible: keyboardUserRef.current
      }));
    };

    const handleFocusOut = () => {
      setAccessibilityState(prev => ({
        ...prev,
        isFocusVisible: false
      }));
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [respectFocusManagement]);

  // Skip links functionality
  useEffect(() => {
    if (!skipLinks) return;

    const handleSkipLink = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && event.target instanceof HTMLAnchorElement) {
        const href = event.target.getAttribute('href');
        if (href?.startsWith('#')) {
          event.preventDefault();
          const target = document.querySelector(href);
          if (target) {
            focusElement(target);
            announce(`Skipped to ${target.getAttribute('aria-label') || 'content'}`);
          }
        }
      }
    };

    document.addEventListener('keydown', handleSkipLink);
    return () => document.removeEventListener('keydown', handleSkipLink);
  }, [skipLinks]);

  // Announce function
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const regionId = `animation-live-${priority}`;
    const region = document.getElementById(regionId);
    
    if (region) {
      // Clear and then set the message to ensure it's announced
      region.textContent = '';
      setTimeout(() => {
        region.textContent = message;
      }, 100);
      
      setAccessibilityState(prev => ({
        ...prev,
        announcements: [...prev.announcements.slice(-4), message] // Keep last 5
      }));
    }
  }, []);

  // Focus element function
  const focusElement = useCallback((element: Element | null) => {
    if (element && element instanceof HTMLElement) {
      element.focus();
      
      // Ensure the element is visible
      element.scrollIntoView({
        behavior: isReducedMotion ? 'auto' : 'smooth',
        block: 'center'
      });
    }
  }, [isReducedMotion]);

  // Skip to content function
  const skipToContent = useCallback(() => {
    const mainContent = document.querySelector('main') || 
                       document.querySelector('[role="main"]') ||
                       document.querySelector('#main-content');
    
    if (mainContent) {
      focusElement(mainContent);
      announce('Skipped to main content');
    }
  }, [focusElement, announce]);

  // Focus visible controls
  const enableFocusVisible = useCallback(() => {
    setAccessibilityState(prev => ({ ...prev, isFocusVisible: true }));
  }, []);

  const disableFocusVisible = useCallback(() => {
    setAccessibilityState(prev => ({ ...prev, isFocusVisible: false }));
  }, []);

  return {
    ...accessibilityState,
    announce,
    focusElement,
    skipToContent,
    enableFocusVisible,
    disableFocusVisible
  };
};

/**
 * Hook for creating accessible interactive elements
 */
export const useAccessibleInteraction = (
  element: React.RefObject<HTMLElement>,
  options: {
    onActivate?: () => void;
    announceOnActivate?: string;
    role?: string;
  } = {}
) => {
  const { announce } = useAccessibility();
  const { onActivate, announceOnActivate, role = 'button' } = options;

  useEffect(() => {
    const el = element.current;
    if (!el) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate?.();
        if (announceOnActivate) {
          announce(announceOnActivate);
        }
      }
    };

    const handleClick = () => {
      onActivate?.();
      if (announceOnActivate) {
        announce(announceOnActivate);
      }
    };

    // Set up accessibility attributes
    if (!el.hasAttribute('role')) {
      el.setAttribute('role', role);
    }
    if (!el.hasAttribute('tabindex')) {
      el.setAttribute('tabindex', '0');
    }

    el.addEventListener('keydown', handleKeyDown);
    el.addEventListener('click', handleClick);

    return () => {
      el.removeEventListener('keydown', handleKeyDown);
      el.removeEventListener('click', handleClick);
    };
  }, [element, onActivate, announceOnActivate, role, announce]);

  return {
    role,
    tabIndex: 0,
    'aria-label': announceOnActivate
  };
};

export default useAccessibility;